import { useCallback } from 'react';
import { Direction } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { ThreadReadStatus } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { ManualSmsScheduledV1 } from '@frontend/api-manual-scheduled-sms';
import { SMSDataSharedTypes, SMSDataSharedUtils, SMSDataV3, SMSDataV4 } from '@frontend/api-sms-data';
import { getUser } from '@frontend/auth-helpers';
import { useMiniChatShallowStore } from '@frontend/mini-chat-store';
import { useAppScopeStore, useLocationGroupStore } from '@frontend/scope';
import { ThreadSendingAreaStores } from '@frontend/thread-sending-area';
import { sentry } from '@frontend/tracking';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';
import { useAlignCountAndListV4 } from './use-align-count-and-list-v4';

/**
 * A hook that returns a function to execute when a SMSNotification event is received.
 * This doesn't have any WebSocket subscription logic, just the handler. Useful for testing.
 */
export const useSMSDataV3WSHandler = () => {
  const user = getUser();

  useAlignCountAndListV4();
  const { selectedLocationIds } = useAppScopeStore();
  const accessibleLocationIds = useLocationGroupStore.getState().accessibleLocationIds;

  const { upsertSMS, updateSMS, updateThread, updateThreadsArchivedStatus, updateThreadsActionableStatus } =
    SMSDataV3._QueryUpdaters.useQueryUpdaters();
  const smsV4QueryUpdaters = SMSDataV4._QueryUpdaters.useQueryUpdaters();
  const unifiedThreadUpdater = SMSDataV4._QueryUpdaters.useUnifiedThreadUpdater();
  const getUnifiedThreadEnabled = SMSDataSharedUtils.useGetUnifiedThreadEnabled();
  const { updateScheduledSms } = ManualSmsScheduledV1._QueryUpdaters.useQueryUpdaters();
  const { setThreadTypingStatus } = ThreadSendingAreaStores.useTypingIndicatorStore();
  const { isSmsMessageItem } = SMSDataSharedUtils.threadItemsUtils;
  const { resetThreadUnreadCount } = useMiniChatShallowStore('resetThreadUnreadCount');

  const v4ThreadCountUpdater = useCallback(
    async ({
      threadIds,
      locationId,
      newStatuses,
    }: {
      threadIds: string[];
      locationId: string;
      newStatuses: SMSDataSharedTypes.CountThreadsStatusUpdate[];
    }) => {
      newStatuses.forEach((newStatus) => {
        smsV4QueryUpdaters.updateThreadCounts({
          threadIds,
          locationId,
          newStatus,
          isOptimistic: false,
        });
      });
    },
    [smsV4QueryUpdaters]
  );

  const wsEventHandler = useCallback<GetWebsocketEventHandler<'SMSNotification'>>(
    async (event) => {
      const { params } = event;
      if (
        !!params.location_id &&
        !accessibleLocationIds.includes(params.location_id) &&
        accessibleLocationIds.includes('40774e48-c299-4c91-bcad-7d88b20f4cca')
      ) {
        // Adding this check as the 40774e48-c299-4c91-bcad-7d88b20f4cca location is facing the sms notification location mismatch issue and we want some logs specific to that location
        // This is a temporary check and should be removed once the issue is resolved
        sentry.error({
          error: 'location ids not matching',
          topic: 'notifications',
          team: 'fe-messaging',
          severityLevel: 'error',
          addContext: {
            name: 'location_mismatch',
            context: {
              event,
              selectedLocationIds,
              accessibleLocationIds,
            },
          },
        });
      }
      switch (params.action) {
        case 'inbound':
        case 'inboundThreadUpdate':
        case 'status':
        case 'outbound':
          // Do not handle these ^ updates here, they are handled in the v4 SMS WS handler
          break;
        case 'smsTagApplied':
          // skip updating unifiedThread. It uses v4 tag mutations which publish only INBOX_THREAD_ITEM_TAG events
          updateSMS({
            matchValues: {
              id: params.sms_id,
              threadId: params.thread_ids?.length ? params.thread_ids[0] : undefined,
              locationId: params.location_id,
            },
            newValues: (oldSms) => {
              const newTags = Array.from(new Set([...oldSms.tags, params.tag_id]));
              return {
                ...oldSms,
                tags: newTags,
                tagsDetailed: newTags.map((tagId) => ({
                  tagId,
                  smsId: oldSms.id,
                  smsCreatedAt: oldSms.createdAt,
                  appliedBy: params.weave_user_id,
                })),
              };
            },
            newThreadValues: {
              tagId: params.tag_id,
              tagInfo: {
                tagId: params.tag_id,
                smsId: params.sms_id,
                smsCreatedAt: params.sms_created_at,
                appliedBy: params.weave_user_id,
              },
            },
            skipInvalidation: true,
          });
          smsV4QueryUpdaters.upsertThread({
            matchValues: {
              threadId: params.thread_ids?.[0] ?? '',
              locationId: params.location_id,
            },
            updater: (thread) => {
              if (!thread) return undefined;

              return {
                ...thread,
                uniqueTags: SMSDataSharedUtils.dedupeSMSTagsArray([
                  ...(thread.uniqueTags ?? []),
                  {
                    tagId: params.tag_id,
                    smsId: params.sms_id,
                    smsCreatedAt: params.sms_created_at,
                    appliedBy: params.weave_user_id,
                  },
                ]),
              };
            },
            isOptimistic: false,
          });
          break;
        case 'smsTagDismissed':
          {
            // skip updating unifiedThread. It uses v4 tag mutations which publish only INBOX_THREAD_ITEM_TAG events
            updateSMS({
              matchValues: {
                id: params.sms_id,
                threadId: params.thread_ids?.length ? params.thread_ids[0] : undefined,
                locationId: params.location_id,
              },
              newValues: (oldSms) => ({
                ...oldSms,
                tags: oldSms.tags.filter((tagId) => tagId !== params.tag_id),
                tagsDetailed: oldSms.tagsDetailed?.filter(({ tagId }) => tagId !== params.tag_id),
              }),
              newThreadValues: {
                tagId: params.tag_id,
                isDismissed: true,
                tagInfo: {
                  tagId: params.NextSMSWTag.tag_id,
                  smsId: params.NextSMSWTag.sms_id,
                  smsCreatedAt: params.NextSMSWTag.sms_created_at,
                  appliedBy: params.NextSMSWTag.applied_by,
                },
              },
              skipInvalidation: true,
            });
            const newTag = params.NextSMSWTag;
            smsV4QueryUpdaters.upsertThread({
              matchValues: {
                threadId: params.thread_ids?.[0] ?? '',
                locationId: params.location_id,
              },
              updater: (oldThread) => {
                if (!oldThread || !oldThread.uniqueTags) return undefined;
                const existingTags = oldThread.uniqueTags;
                if (!existingTags) return undefined;

                const shouldUpdateTag = !!newTag.tag_id;
                if (shouldUpdateTag) {
                  return {
                    ...oldThread,
                    uniqueTags: existingTags.map((tag) =>
                      tag.tagId === newTag.tag_id
                        ? {
                            tagId: newTag.tag_id,
                            smsId: newTag.sms_id,
                            smsCreatedAt: newTag.sms_created_at,
                            appliedBy: newTag.applied_by,
                          }
                        : tag
                    ),
                  };
                }

                return {
                  ...oldThread,
                  uniqueTags: existingTags.filter((tag) => tag.tagId !== params.tag_id),
                };
              },
              isOptimistic: false,
            });
          }
          break;
        case 'archived':
        case 'unarchived':
          if (params.thread_ids?.length && !!params.location_id) {
            const locationId = params.location_id;
            if (getUnifiedThreadEnabled) {
              params.thread_ids.forEach(async (threadId) => {
                await unifiedThreadUpdater.upsertUnifiedThread({
                  matchValues: {
                    threadId,
                    locationId,
                  },
                  updater: (thread) =>
                    thread
                      ? {
                          ...thread,
                          isArchived: params.action === 'archived',
                        }
                      : undefined,
                  isOptimistic: false,
                });
              });
            } else {
              updateThreadsArchivedStatus({
                threadIds: params.thread_ids,
                locationId: params.location_id,
                groupIds: [params.location_id],
                archived: params.action === 'archived',
              });
              params.thread_ids.forEach((threadId) => {
                smsV4QueryUpdaters.upsertThread({
                  matchValues: {
                    threadId,
                    locationId,
                  },
                  updater: (oldThread) =>
                    oldThread ? { ...oldThread, isArchived: params.action === 'archived' } : undefined,
                  isOptimistic: false,
                });
              });
            }
          }
          break;
        case 'bulkMarkRead':
        case 'markedRead':
          if (getUnifiedThreadEnabled) {
            params.thread_ids?.forEach((threadId) => {
              resetThreadUnreadCount(threadId);
              setTimeout(
                () => {
                  unifiedThreadUpdater.upsertUnifiedThread({
                    matchValues: {
                      threadId: threadId,
                      locationId: params.location_id ?? '',
                    },
                    updater: (oldThreadData) => {
                      if (oldThreadData) {
                        // Determine isReplied based on the first message's direction
                        const firstItem = oldThreadData.items?.[0];
                        const firstSms = firstItem && isSmsMessageItem(firstItem) ? firstItem.smsMessage : undefined;
                        const shouldBeReplied =
                          firstSms?.direction === Direction.DIRECTION_OUTBOUND && !firstSms?.autogeneratedBy;

                        return {
                          ...oldThreadData,
                          status: ThreadReadStatus.THREAD_READ_STATUS_READ,
                          isReplied: shouldBeReplied,
                        };
                      }
                      return oldThreadData;
                    },
                    isOptimistic: false,
                  });
                }, // Wait for a bit if this is marked read by the classification flow on the BE to avoid race conditions
                // with the `inbound` event
                !params.weave_user_id ? 300 : 0
              );
            });
          } else {
            params.thread_ids?.forEach((threadId) => {
              updateThread({
                matchValues: {
                  id: threadId,
                  locationId: params.location_id,
                },
                newValues: {
                  status: 'read',
                },
              });
              resetThreadUnreadCount(threadId);
              const locationId = params.location_id;
              if (locationId) {
                setTimeout(
                  () => {
                    smsV4QueryUpdaters.upsertThread({
                      matchValues: {
                        threadId: threadId,
                        locationId,
                      },
                      updater: (oldThread) =>
                        oldThread
                          ? {
                              ...oldThread,
                              status: 'read',
                            }
                          : undefined,
                      isOptimistic: false,
                    });
                  },
                  // Wait for a bit if this is marked read by the classification flow on the BE to avoid race conditions
                  // with the `inbound` event
                  !params.weave_user_id ? 300 : 0
                );
              }
            });
          }

          if (params.location_id) {
            v4ThreadCountUpdater({
              threadIds: params.thread_ids ?? [],
              locationId: params.location_id,
              newStatuses: ['read'],
            });
          }

          break;
        case 'bulkMarkUnread':
        case 'markedUnread':
          if (getUnifiedThreadEnabled) {
            params.thread_ids?.forEach((threadId) => {
              unifiedThreadUpdater.upsertUnifiedThread({
                matchValues: {
                  threadId,
                  locationId: params.location_id ?? '',
                },
                updater: (thread) =>
                  thread
                    ? {
                        ...thread,
                        status: ThreadReadStatus.THREAD_READ_STATUS_NEW,
                        actionable: true, // marking a thread as unread should make it actionable
                      }
                    : undefined,
                isOptimistic: false,
              });
            });
          } else {
            params.thread_ids?.forEach((threadId) => {
              updateThread({
                matchValues: {
                  id: threadId,
                  locationId: params.location_id,
                },
                newValues: {
                  status: 'new',
                },
              });
              const locationId = params.location_id;
              if (locationId) {
                smsV4QueryUpdaters.upsertThread({
                  matchValues: {
                    threadId,
                    locationId,
                  },
                  updater: (oldThread) => (oldThread ? { ...oldThread, status: 'new', actionable: true } : undefined), // marking a thread as unread should make it actionable
                  isOptimistic: false,
                });
              }
            });
          }

          if (params.location_id) {
            v4ThreadCountUpdater({
              threadIds: params.thread_ids ?? [],
              locationId: params.location_id,
              newStatuses: ['unread'],
            });
          }
          break;
        case 'readStatusChange':
          if (getUnifiedThreadEnabled) {
            params.thread_ids?.forEach((threadId) => {
              if (params.status === 'read') {
                resetThreadUnreadCount(threadId);
              }
              unifiedThreadUpdater.upsertUnifiedThread({
                matchValues: {
                  threadId,
                  locationId: params.location_id ?? '',
                },
                updater: (thread) =>
                  thread
                    ? {
                        ...thread,
                        status:
                          params.status === 'read'
                            ? ThreadReadStatus.THREAD_READ_STATUS_READ
                            : ThreadReadStatus.THREAD_READ_STATUS_NEW,
                      }
                    : undefined,
                isOptimistic: false,
              });
            });
          } else {
            params.thread_ids?.forEach((threadId) => {
              updateThread({
                matchValues: {
                  id: threadId,
                  locationId: params.location_id,
                },
                newValues: {
                  status: params.status,
                },
              });
              if (params.status === 'read') {
                resetThreadUnreadCount(threadId);
              }
              const locationId = params.location_id;
              if (locationId) {
                smsV4QueryUpdaters.upsertThread({
                  matchValues: {
                    threadId,
                    locationId,
                  },
                  updater: (oldThread) => (oldThread ? { ...oldThread, status: params.status } : undefined),
                  isOptimistic: false,
                });
              }
            });
          }
          break;
        case 'typing':
          if (user?.userID && params.weave_user_id === user?.userID) break;
          setThreadTypingStatus({
            locationId: params.location_id,
            threadId: params.thread_id,
            userId: params.weave_user_id,
            firstName: params.first_name,
            lastName: params.last_name,
            isTyping: params.is_typing,
          });
          break;
        case 'updateActionableStatus':
          if (params.location_id && params.actionable !== undefined) {
            if (getUnifiedThreadEnabled) {
              params.thread_ids?.forEach((threadId) => {
                unifiedThreadUpdater.upsertUnifiedThread({
                  matchValues: {
                    threadId: threadId,
                    locationId: params.location_id ?? '',
                  },
                  updater: (thread) =>
                    thread
                      ? {
                          ...thread,
                          actionable: !!params.actionable,
                          status:
                            params.actionable === false && thread.status === ThreadReadStatus.THREAD_READ_STATUS_NEW
                              ? ThreadReadStatus.THREAD_READ_STATUS_READ
                              : thread.status,
                        }
                      : undefined,
                  isOptimistic: false,
                });
              });
            } else {
              updateThreadsActionableStatus({
                locationId: params.location_id,
                actionable: params.actionable,
                threadIds: params.thread_ids ?? [],
              });
            }

            // Update V4 threads with actionable status
            params.thread_ids?.forEach((threadId) => {
              smsV4QueryUpdaters.upsertThread({
                matchValues: {
                  threadId,
                  locationId: params.location_id!,
                },
                updater: (oldThread) => {
                  const updatedThread = oldThread
                    ? {
                        ...oldThread,
                        actionable: !!params.actionable,
                        status: params.actionable === false ? 'read' : oldThread.status, // if a thread is marked not-actionable/complete, it should be read.
                      }
                    : undefined;
                  return updatedThread;
                },
                isOptimistic: false,
              });
            });

            const newStatus = params.actionable ? 'actionable' : 'not-actionable';
            v4ThreadCountUpdater({
              threadIds: params.thread_ids ?? [],
              locationId: params.location_id,
              newStatuses: [newStatus],
            });
          }
          break;
      }
    },
    [
      resetThreadUnreadCount,
      setThreadTypingStatus,
      smsV4QueryUpdaters,
      updateSMS,
      updateScheduledSms,
      updateThread,
      updateThreadsActionableStatus,
      updateThreadsArchivedStatus,
      upsertSMS,
      user?.userID,
      v4ThreadCountUpdater,
    ]
  );

  return wsEventHandler;
};

export const useSMSDataV3WSSubscription = () => {
  const handler = useSMSDataV3WSHandler();
  return useWebsocketEventSubscription('SMSNotification', handler);
};
